// Where the sky's catalogues and maps sit in the scene.
//
// The scene is J2000 ecliptic (src/orbits/earthframe.js): +X the vernal
// equinox, +Y the ecliptic north pole, -Z ecliptic longitude 90. Star
// catalogues give J2000 equatorial positions (x towards RA 0, y towards RA 6h,
// z the north celestial pole). They reach the scene the way the Earth's own
// axes do: (x, y, z)_eq -> (x, z, -y), then a turn of -obliquity about X, so
// the celestial pole lands at (0, cos e, -sin e) and the ecliptic, where the
// planets move, runs through the zodiac.
//
// Until October 2026 the stars, the constellation lines and the deep-sky
// photos were placed as (x, z, y), which mirrors the sky (Betelgeuse to the
// lower right of Orion) and lays the celestial equator on the ecliptic, and
// the Milky Way panorama was hung in its own galactic frame, with the band
// along the planets' orbits. Everything that places something on the sky
// goes through this file, so they agree with each other and with the planets.
//
// Galactic coordinates: the IAU 1958 system as realised in ICRS, the matrix
// A_G of ESA 1997, The Hipparcos and Tycho Catalogues, Vol. 1, sec. 1.5.3
// (eq. 1.5.11), r_gal = A_G' r_icrs.
import * as THREE from 'three';
import { EARTH_OBLIQUITY } from '../orbits/earthframe.js';

const c = Math.cos(EARTH_OBLIQUITY), s = Math.sin(EARTH_OBLIQUITY);

// Equatorial unit vector (or any equatorial vector) to the scene.
export function equatorialToScene(x, y, z, out = new THREE.Vector3()) {
  return out.set(x, z * c - y * s, -(y * c + z * s));
}

// Right ascension in hours and declination in degrees to a scene direction.
export function raDecToScene(raHours, decDeg, out = new THREE.Vector3()) {
  const ra = raHours * (Math.PI / 12), dec = decDeg * (Math.PI / 180);
  const cd = Math.cos(dec);
  return equatorialToScene(cd * Math.cos(ra), cd * Math.sin(ra), Math.sin(dec), out);
}

// The north celestial pole in the scene: "up" for anything drawn on the sky
// with north at the top.
export const CELESTIAL_NORTH = equatorialToScene(0, 0, 1);

// Row-major: Matrix3.set takes rows.
export const EQUATORIAL_TO_SCENE = new THREE.Matrix3().set(
  1, 0, 0,
  0, -s, c,
  0, -c, -s,
);
export const SCENE_TO_EQUATORIAL = EQUATORIAL_TO_SCENE.clone().transpose();
const EQUATORIAL_TO_GALACTIC = new THREE.Matrix3().set(
  -0.0548755604, -0.8734370902, -0.4838350155,
  0.4941094279, -0.4448296300, 0.7469822445,
  -0.8676661490, -0.1980763734, 0.4559837762,
);
export const SCENE_TO_GALACTIC = EQUATORIAL_TO_GALACTIC.clone().multiply(SCENE_TO_EQUATORIAL);
export const GALACTIC_TO_SCENE = SCENE_TO_GALACTIC.clone().transpose();

// Galactic (l, b in degrees) to a scene direction.
export function galacticToScene(lDeg, bDeg, out = new THREE.Vector3()) {
  const l = lDeg * (Math.PI / 180), b = bDeg * (Math.PI / 180);
  return out.set(Math.cos(b) * Math.cos(l), Math.cos(b) * Math.sin(l), Math.sin(b)).applyMatrix3(GALACTIC_TO_SCENE);
}

// Equirectangular sky maps. skyMapUv (below) reads longitude as
// atan(d.y, d.x) and latitude as asin(d.z) in the map's frame `frame` (from
// the scene), at u = u0 + longitude / 2pi and v = 0.5 + latitude / pi (v = 1
// is the image's top row, three.js flips it on upload).
export const SKY_MAPS = {
  // NASA SVS Deep Star Maps 2020 as written by scripts/build-sky-background.py:
  // RA 0 at the left edge increasing to the right, north at the top.
  icrs: { frame: SCENE_TO_EQUATORIAL.clone(), u0: 0 },
  // stars_8k.jpg (the loading backdrop, built from NASA SVS Deep Star Maps in
  // the old Solar System Scope layout): galactic, l = 0 at the centre and
  // increasing to the left, the south galactic pole at the top (measured:
  // its stars land on the HYG stars only so). Longitude and latitude both
  // run backwards, which is a half turn about the map's x axis.
  galacticPanorama: { frame: new THREE.Matrix3().set(1, 0, 0, 0, -1, 0, 0, 0, -1).multiply(SCENE_TO_GALACTIC), u0: 0.5 },
};

// Shared by every material that samples the sky map (the skybox, the black
// holes' lensed sky, the UFO's reflection): spread into a material's uniforms
// so a change of map reaches them all.
export const skyMapUniforms = {
  uSkyFrame: { value: new THREE.Matrix3() },
  uSkyU0: { value: 0 },
};

export function useSkyMap(name) {
  const map = SKY_MAPS[name];
  skyMapUniforms.uSkyFrame.value.copy(map.frame);
  skyMapUniforms.uSkyU0.value = map.u0;
}
useSkyMap('galacticPanorama');

// The lookup itself. skyMapSample takes the longitude's wrap out of the
// derivatives, so the mip level does not drop to the smallest along the seam
// where atan jumps from pi to -pi (it would draw a one pixel line).
export const SKY_MAP_GLSL = /* glsl */`
  uniform mat3 uSkyFrame;
  uniform float uSkyU0;
  vec2 skyMapUv(vec3 worldDir) {
    vec3 d = normalize(uSkyFrame * worldDir);
    return vec2(uSkyU0 + atan(d.y, d.x) * 0.15915494, 0.5 + asin(clamp(d.z, -1.0, 1.0)) * 0.31830989);
  }
  vec4 skyMapSample(sampler2D map, vec2 uv, vec2 dx, vec2 dy) {
    return textureGrad(map, uv, dx, dy);
  }
  void skyMapGrad(vec2 uv, out vec2 dx, out vec2 dy) {
    dx = dFdx(uv);
    dy = dFdy(uv);
    dx.x -= floor(dx.x + 0.5);
    dy.x -= floor(dy.x + 0.5);
  }
  vec4 skyMapSample(sampler2D map, vec2 uv) {
    vec2 dx, dy;
    skyMapGrad(uv, dx, dy);
    return textureGrad(map, uv, dx, dy);
  }
`;
